"use client";

import { type ReactNode, use, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import type { BeaconData } from "./types";
import { StoreTrackingScripts } from "./StoreTrackingScripts";
import { STAGE } from "@local/utils/src/services";
import Cookies from "js-cookie";
import { ensurePosthogClient, isPostHogEnabled, registerPostHogProperties, trackPostHog } from "../scripts/posthog";
import { trackAction } from "../trackActions";
import { readUpsellChannel } from "../upsellChannelStorage";
import { VV10_COOKIE, VV10_FEATURE_FLAG, VV10_VARIANTS, type Vv10Variant } from "../experiments/skuPricePremium";

/** Params that are data-only — available via window globals / cookies,
 *  so we strip them from the URL instead of injecting and re-stripping. */
const DATA_ONLY_PARAMS = ["vb", "country", "currency"
// "nuclick" is intentionally excluded — NuClickBeacon owns its removal
// after GTM has had a chance to read it on the initial page_view
];

/** Kick off the deferred PostHog init. Idempotent — the helper is backed by
 *  a module-level singleton promise. No-op in apps that didn't call
 *  enablePostHog() in their instrumentation-client.ts. */
const bootPosthog = () => {
  if (!isPostHogEnabled()) return;
  ensurePosthogClient().catch(() => {
    // ensurePosthogClient already logs and resets internally
  });
};
interface Props {
  dataPromise: Promise<BeaconData | null>;
  buildCheckoutUrl?: boolean;
  children: ReactNode;
}
export const TrackingLoader = ({
  dataPromise,
  buildCheckoutUrl,
  children
}: Props) => {
  const data = use(dataPromise);
  const urlParams = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const itemId = searchParams.get("item_id");
  const [hydrated, setHydrated] = useState(false);

  // ── Channel Sync — useLayoutEffect ensures window.channel is set
  // before paint and before other useEffects fire ──
  // Segment-equality matches (not substring) so /baerskin-hoodie or
  // similar product URLs don't accidentally classify as a channel.
  const channelSegments = pathname.split("/").filter(Boolean);
  const channelSection = channelSegments[1] ?? "";
  const upsellIdFromPath = channelSegments[2] ?? null;
  // Upsell pages have no channel in the URL — recover it from the
  // localStorage record written at upsell-id prefetch time so the
  // channel doesn't collapse to "store" here.
  const isUpsellPath = channelSection === "up";
  const upsellChannel = isUpsellPath && upsellIdFromPath ? readUpsellChannel(upsellIdFromPath) : null;
  const channelId = upsellChannel ? upsellChannel : urlParams.channel ? urlParams.channel as string : "store";
  useLayoutEffect(() => {
    window.channel = channelId;
  }, [channelId]);

  // PostHog loads lazily — register channel via withPostHogClient
  useEffect(() => {
    registerPostHogProperties({
      channel: channelId
    });
  }, [channelId]);

  // ── vv10 (per-SKU price premium, DEV-9783) experiment attribution ──
  // Exposure fires on the backpack-hoodie LP CHECKOUT only — the single
  // surface where the two arms see different prices. Firing on the LP
  // home (where the cookie is already assigned) would dilute the
  // experiment with visitors who never reached a priced surface. The
  // cookie only exists for visitors the middleware bucketed, so its
  // presence is the "in experiment" signal.
  const isVv10ExposurePath = (() => {
    const segments = pathname.split("/").filter(Boolean);
    const [, section, channel, subpage] = segments;
    return section === "lp" && channel === "backpack-hoodie" && subpage === "checkout";
  })();

  // The ?vv10= QA override lands as a cookie via middleware on the same
  // navigation — depend on it so an in-place override change (same
  // pathname, new query) re-fires attribution with the new variant.
  const vv10Override = searchParams.get(VV10_COOKIE);
  useEffect(() => {
    const vv10 = Cookies.get(VV10_COOKIE);
    if (!vv10 || !isVv10ExposurePath || !VV10_VARIANTS.includes(vv10 as Vv10Variant)) {
      return;
    }
    const attribute = () => {
      // `$feature/<flag>` is what PostHog's experiment engine reads to
      // attribute downstream metric events (purchase etc.) to a variant
      // (house convention); the bare `vv10` stays for ad-hoc breakdowns.
      registerPostHogProperties({
        vv10,
        [`$feature/${VV10_FEATURE_FLAG}`]: vv10
      });
      trackPostHog("$feature_flag_called", {
        $feature_flag: VV10_FEATURE_FLAG,
        $feature_flag_response: vv10
      });
      trackAction("split_test", {
        experiment: VV10_FEATURE_FLAG,
        variant: vv10
      });
    };
    if (window.posthog?.__loaded) {
      attribute();
      return;
    }
    const handler = () => attribute();
    window.addEventListener("posthog-ready", handler, {
      once: true
    });
    return () => window.removeEventListener("posthog-ready", handler);
  }, [isVv10ExposurePath, pathname, vv10Override]);

  // ── Checkout URL setup (LP pages only) ──
  const syncCheckoutUrl = useCallback(() => {
    if (buildCheckoutUrl && pathname.includes("/lp/")) {
      const market = urlParams.market as string | undefined;
      const channel_0 = urlParams.channel as string | undefined;
      const origin = window.location.origin;
      if (market && channel_0) {
        let checkoutHref = `${origin}/${market}/lp/${channel_0}/checkout`;
        if (itemId) {
          checkoutHref = `${checkoutHref}?item_id=${encodeURIComponent(itemId)}`;
        }
        window.checkout_url = checkoutHref;
      } else {
        delete window.checkout_url;
      }
    } else {
      delete window.checkout_url;
    }
  }, [buildCheckoutUrl, pathname, urlParams, itemId]);

  // ── Data Hydration ──
  useEffect(() => {
    if (!data) {
      delete window.$r;
      delete window.$vb;
      syncCheckoutUrl();
      // No URL rebuild to apply; PostHog can init against the current URL.
      bootPosthog();
      window.dispatchEvent(new Event("beacon:ready"));
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time hydration gate after syncing external state
      setHydrated(true);
      return;
    }
    window.$r = {
      bot: data.bot,
      ip: data.ip,
      isp: data.isp,
      loc: data.loc ?? undefined,
      geo: data.geo ?? undefined,
      proxy: data.proxy,
      ua: data.ua ?? undefined,
      w: data.weather,
      vibes: data.vibes ?? undefined
    };
    if (data.vibes) {
      window.$vb = data.vibes;
    } else {
      delete window.$vb;
    }

    // Push rebuilt URL params (utm, click IDs, dodo, etc.) to the browser.
    // Data-only params are stripped before writing — no inject/strip cycle.
    const url = new URL(window.location.href);
    url.search = data.url.search;
    for (const param of DATA_ONLY_PARAMS) {
      url.searchParams.delete(param);
    }
    const currentHistoryState = window.history.state ?? {};
    window.history.replaceState(currentHistoryState, "", url.pathname + url.search + url.hash);

    // PostHog must not be initialised before the URL rewrite above, otherwise
    // the SDK's first event (identify / autocapture / pageview) is captured
    // against the pre-rebuild URL and PostHog locks $session_entry_url and
    // $initial_utm_* to a URL missing utm_medium for fbclid-only / referrer-only
    // landings. instrumentation-client.ts only calls enablePostHog(); the actual
    // init happens here.
    bootPosthog();
    registerPostHogProperties({
      vibes: data.vibes
    });
    syncCheckoutUrl();
    window.dispatchEvent(new Event("beacon:ready"));
    setHydrated(true);
  }, [data, syncCheckoutUrl, channelId]);

  // Wait for hydration before mounting anything
  if (!hydrated) return null;
  if (STAGE === "dev") {
    return <>{children}</>;
  }
  return <>
      {data && <StoreTrackingScripts data={data} />}
      {children}
    </>;
};